import React, { useState, useEffect, useCallback } from "react";
import DateRow from "./DateRow";

/* ── Mock data ── */
const CLIENTS = ["Al Noor Real Estate", "ABC Builders"];

const PROJECTS = ["Website Redesign", "Mobile App Development"];

const DEPARTMENTS = ["Design", "Marketing", "Content"];

const DEPT_RESOURCES = {
  Design:    ["James Wilson", "Justin Kale"],
  Marketing: ["Martin Warner", "Jack Clerk"],
  Content:   ["Ruby Hastings"],
};

const INITIAL_DATE_ROWS = [
  {
    id: Date.now() + 1,
    startDate: "2026-04-10",
    endDate:   "2026-04-13",
    notes:     "James will be working on Nike project",
    conflict:  true,
  },
  {
    id: Date.now() + 2,
    startDate: "2026-04-14",
    endDate:   "2026-04-14",
    notes:     "James will be working on Apple project",
    conflict:  false,
  },
];

/* ── Shared input style ── */
const fieldBase = {
  width: "100%",
  padding: "9px 12px",
  borderRadius: 8,
  border: "1px solid #e2e8f0",
  fontSize: 13,
  color: "#334155",
  background: "#fff",
  outline: "none",
  fontFamily: "inherit",
  boxSizing: "border-box",
  transition: "border-color 0.15s, box-shadow 0.15s",
};

const labelStyle = {
  display: "block",
  fontSize: 12,
  fontWeight: 600,
  color: "#475569",
  marginBottom: 5,
};

function FieldLabel({ children, required }) {
  return (
    <label style={labelStyle}>
      {children}
      {required && <span style={{ color: "#ef4444", marginLeft: 2 }}>*</span>}
    </label>
  );
}

function SelectField({ label, value, onChange, options, placeholder, required, disabled }) {
  return (
    <div>
      <FieldLabel required={required}>{label}</FieldLabel>
      <div style={{ position: "relative" }}>
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          style={{
            ...fieldBase,
            appearance: "none",
            WebkitAppearance: "none",
            paddingRight: 32,
            cursor: disabled ? "not-allowed" : "pointer",
            background: disabled ? "#f8fafc" : "#fff",
            color: value ? "#334155" : "#94a3b8",
          }}
          onFocus={(e) => (e.currentTarget.style.borderColor = "#6366f1")}
          onBlur={(e) => (e.currentTarget.style.borderColor = "#e2e8f0")}
        >
          <option value="">{placeholder || `Select ${label}`}</option>
          {options.map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
        <i
          className="fa fa-angle-down"
          style={{
            position: "absolute",
            right: 11,
            top: "50%",
            transform: "translateY(-50%)",
            color: "#94a3b8",
            fontSize: 13,
            pointerEvents: "none",
          }}
        />
      </div>
    </div>
  );
}

/* ── Detect overlapping date ranges ── */
function hasOverlap(rows) {
  const parsed = rows.map((r) => ({
    start: new Date(r.startDate),
    end:   new Date(r.endDate),
  }));
  return rows.map((_, i) => {
    for (let j = 0; j < parsed.length; j++) {
      if (i === j) continue;
      if (
        parsed[i].start <= parsed[j].end &&
        parsed[i].end   >= parsed[j].start
      )
        return true;
    }
    return false;
  });
}

/* ══════════════════════════════════════════════════
   AllocateResourceModal
════════════════════════════════════════════════════ */
const AllocateResourceModal = ({ isOpen, onClose }) => {
  const [form, setForm] = useState({
    client:     "",
    project:    "",
    department: "",
    resource:   "",
    task:       "",
  });

  const [dateRows,   setDateRows]   = useState(INITIAL_DATE_ROWS);
  const [submitting, setSubmitting] = useState(false);
  const [visible,    setVisible]    = useState(false); // animation

  /* ── Animate in / out ── */
  useEffect(() => {
    if (isOpen) {
      // micro-tick so CSS transition fires
      requestAnimationFrame(() => setVisible(true));
    } else {
      setVisible(false);
    }
  }, [isOpen]);

  /* ── Close on Escape ── */
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  /* ── Block body scroll while open ── */
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  /* ── Derived resources filtered by dept ── */
  const availableResources = form.department
    ? DEPT_RESOURCES[form.department] || []
    : Object.values(DEPT_RESOURCES).flat();

  /* ── Field change handlers ── */
  const setField = (key) => (val) => {
    setForm((f) => {
      const next = { ...f, [key]: val };
      if (key === "department") next.resource = ""; // reset resource on dept change
      return next;
    });
  };

  /* ── Date row handlers ── */
  const handleRowChange = useCallback((index, field, value) => {
    setDateRows((rows) => {
      const next = rows.map((r, i) =>
        i === index ? { ...r, [field]: value } : r
      );
      // recompute conflicts
      const conflicts = hasOverlap(next);
      return next.map((r, i) => ({ ...r, conflict: conflicts[i] }));
    });
  }, []);

  const handleRowDelete = useCallback((index) => {
    setDateRows((rows) => {
      const next = rows.filter((_, i) => i !== index);
      const conflicts = hasOverlap(next);
      return next.map((r, i) => ({ ...r, conflict: conflicts[i] }));
    });
  }, []);

  const handleAddDate = () => {
    setDateRows((rows) => {
      const next = [
        ...rows,
        {
          id:        Date.now(),
          startDate: "",
          endDate:   "",
          notes:     "",
          conflict:  false,
        },
      ];
      const conflicts = hasOverlap(next);
      return next.map((r, i) => ({ ...r, conflict: conflicts[i] }));
    });
  };

  /* ── Submit ── */
  const handleSubmit = () => {
    setSubmitting(true);
    const payload = { ...form, dateAllocations: dateRows };
    console.log("Allocate Resource Payload:", payload);
    setTimeout(() => {
      setSubmitting(false);
      onClose();
    }, 800);
  };

  const hasConflicts = dateRows.some((r) => r.conflict);

  if (!isOpen) return null;

  return (
    /* Overlay */
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position:        "fixed",
        inset:           0,
        zIndex:          9999,
        background:      `rgba(15, 23, 42, ${visible ? 0.45 : 0})`,
        display:         "flex",
        alignItems:      "center",
        justifyContent:  "center",
        padding:         "20px 16px",
        transition:      "background 0.25s",
        overflowY:       "auto",
      }}
    >
      {/* Modal panel */}
      <div
        style={{
          background:    "#fff",
          borderRadius:  16,
          boxShadow:     "0 25px 60px rgba(0,0,0,0.18)",
          width:         "100%",
          maxWidth:      820,
          padding:       "28px 32px",
          position:      "relative",
          transform:     visible ? "scale(1) translateY(0)" : "scale(0.95) translateY(16px)",
          opacity:       visible ? 1 : 0,
          transition:    "transform 0.25s cubic-bezier(.34,1.36,.64,1), opacity 0.2s",
          maxHeight:     "90vh",
          overflowY:     "auto",
        }}
      >
        {/* ── Modal header ── */}
        <div
          style={{
            display:        "flex",
            alignItems:     "center",
            justifyContent: "space-between",
            marginBottom:   24,
          }}
        >
          <div>
            <h4
              style={{
                margin:       0,
                fontSize:     20,
                fontWeight:   800,
                color:        "#1e293b",
                letterSpacing: "-0.01em",
              }}
            >
              Add Resource
            </h4>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: "#94a3b8" }}>
              Assign a resource and define their allocation schedule
            </p>
          </div>

          <button
            onClick={onClose}
            style={{
              width:          36,
              height:         36,
              borderRadius:   "50%",
              border:         "1px solid #e2e8f0",
              background:     "#f8fafc",
              display:        "flex",
              alignItems:     "center",
              justifyContent: "center",
              cursor:         "pointer",
              color:          "#64748b",
              fontSize:       16,
              flexShrink:     0,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#fee2e2";
              e.currentTarget.style.color = "#dc2626";
              e.currentTarget.style.borderColor = "#fca5a5";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#f8fafc";
              e.currentTarget.style.color = "#64748b";
              e.currentTarget.style.borderColor = "#e2e8f0";
            }}
          >
            <i className="fa fa-times" />
          </button>
        </div>

        {/* Thin divider */}
        <div style={{ borderTop: "1px solid #f1f5f9", marginBottom: 24 }} />

        {/* ── Form section ── */}
        <div
          style={{
            display:             "grid",
            gridTemplateColumns: "1fr 1fr",
            gap:                 "18px 20px",
            marginBottom:        24,
          }}
        >
          {/* Row 1 */}
          <SelectField
            label="Select Client"
            value={form.client}
            onChange={setField("client")}
            options={CLIENTS}
            required
          />
          <SelectField
            label="Select Project"
            value={form.project}
            onChange={setField("project")}
            options={PROJECTS}
            required
          />

          {/* Row 2 */}
          <SelectField
            label="Select Department"
            value={form.department}
            onChange={setField("department")}
            options={DEPARTMENTS}
            required
          />
          <SelectField
            label="Select Resource"
            value={form.resource}
            onChange={setField("resource")}
            options={availableResources}
            placeholder={
              form.department
                ? availableResources.length
                  ? "Select Resource"
                  : "No resources in this dept"
                : "Select Department first"
            }
            disabled={!form.department}
            required
          />

          {/* Row 3 – full width */}
          <div style={{ gridColumn: "1 / -1" }}>
            <FieldLabel>Task Description</FieldLabel>
            <textarea
              rows={3}
              placeholder="Describe the task or responsibility for this resource…"
              value={form.task}
              onChange={(e) => setField("task")(e.target.value)}
              style={{
                ...fieldBase,
                resize:     "vertical",
                lineHeight: 1.6,
              }}
              onFocus={(e) => (e.currentTarget.style.borderColor = "#6366f1")}
              onBlur={(e)  => (e.currentTarget.style.borderColor = "#e2e8f0")}
            />
          </div>
        </div>

        {/* ── Date allocation section ── */}
        <div
          style={{
            background:   "#f8fafc",
            borderRadius: 12,
            padding:      "20px 20px 16px",
            border:       "1px solid #e2e8f0",
            marginBottom: 24,
          }}
        >
          {/* Section title */}
          <div style={{ marginBottom: 14 }}>
            <h6
              style={{
                margin:      "0 0 4px",
                fontSize:    14,
                fontWeight:  700,
                color:       "#334155",
              }}
            >
              Date Allocation
            </h6>
            <p style={{ margin: 0, fontSize: 12, color: "#94a3b8", lineHeight: 1.5 }}>
              Please fill the below details in order to add date and time for completing
              your resource allocation form.
            </p>
          </div>

          {/* Conflict banner */}
          {hasConflicts && (
            <div
              style={{
                display:      "flex",
                alignItems:   "center",
                gap:          8,
                background:   "#fee2e2",
                border:       "1px solid #fca5a5",
                borderRadius: 8,
                padding:      "10px 14px",
                marginBottom: 14,
                fontSize:     12,
                color:        "#dc2626",
                fontWeight:   500,
              }}
            >
              <i className="fa fa-exclamation-triangle" style={{ fontSize: 14 }} />
              Some date ranges overlap. Please resolve conflicts before submitting.
            </div>
          )}

          {/* Date rows table — Add Date button is always the first tbody row */}
          <div
            style={{
              overflowX:    "auto",
              borderRadius: 8,
              border:       "1px solid #e2e8f0",
              background:   "#fff",
            }}
          >
            <table
              style={{
                borderCollapse: "collapse",
                width:          "100%",
                minWidth:       600,
              }}
            >
              <thead>
                <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
                  {["Start Date", "End Date", "Notes", "Actions"].map((col) => (
                    <th
                      key={col}
                      style={{
                        padding:       "10px 10px",
                        textAlign:     col === "Actions" ? "center" : "left",
                        fontSize:      11,
                        fontWeight:    700,
                        color:         "#94a3b8",
                        background:    "#f8fafc",
                        letterSpacing: "0.04em",
                        textTransform: "uppercase",
                        whiteSpace:    "nowrap",
                      }}
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* ── + Add Date row (always first) ── */}
                <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
                  <td colSpan={4} style={{ padding: "6px 8px" }}>
                    <button
                      onClick={handleAddDate}
                      style={{
                        display:      "flex",
                        alignItems:   "center",
                        justifyContent: "center",
                        gap:          6,
                        width:        "100%",
                        border:       "1.5px dashed #c7d2fe",
                        background:   "#fff",
                        color:        "#6366f1",
                        padding:      "8px 0",
                        borderRadius: 7,
                        fontSize:     12,
                        fontWeight:   600,
                        cursor:       "pointer",
                        transition:   "all 0.15s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background  = "#eef2ff";
                        e.currentTarget.style.borderColor = "#6366f1";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background  = "#fff";
                        e.currentTarget.style.borderColor = "#c7d2fe";
                      }}
                    >
                      <i className="fa fa-plus" style={{ fontSize: 10 }} />
                      Add Date
                    </button>
                  </td>
                </tr>

                {/* ── Data rows ── */}
                {dateRows.map((row, i) => (
                  <DateRow
                    key={row.id}
                    row={row}
                    index={i}
                    onChange={handleRowChange}
                    onDelete={handleRowDelete}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── Divider ── */}
        <div style={{ borderTop: "1px solid #f1f5f9", marginBottom: 20 }} />

        {/* ── Footer ── */}
        <div
          style={{
            display:        "flex",
            justifyContent: "flex-end",
            alignItems:     "center",
            gap:            12,
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding:      "9px 20px",
              borderRadius: 8,
              border:       "1px solid #e2e8f0",
              background:   "#fff",
              color:        "#64748b",
              fontSize:     13,
              fontWeight:   600,
              cursor:       "pointer",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#f8fafc")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
          >
            Cancel
          </button>

          <button
            onClick={handleSubmit}
            disabled={submitting || hasConflicts}
            style={{
              display:      "inline-flex",
              alignItems:   "center",
              gap:          7,
              padding:      "9px 22px",
              borderRadius: 8,
              border:       "none",
              background:   submitting || hasConflicts ? "#a5b4fc" : "#6366f1",
              color:        "#fff",
              fontSize:     13,
              fontWeight:   700,
              cursor:       submitting || hasConflicts ? "not-allowed" : "pointer",
              boxShadow:    "0 2px 8px rgba(99,102,241,0.35)",
              transition:   "background 0.15s",
              letterSpacing: "0.02em",
            }}
            onMouseEnter={(e) => {
              if (!submitting && !hasConflicts)
                e.currentTarget.style.background = "#4f46e5";
            }}
            onMouseLeave={(e) => {
              if (!submitting && !hasConflicts)
                e.currentTarget.style.background = "#6366f1";
            }}
            title={hasConflicts ? "Resolve date conflicts before submitting" : ""}
          >
            {submitting ? (
              <>
                <i className="fa fa-spinner fa-spin" />
                Allocating…
              </>
            ) : (
              <>
                <i className="fa fa-check" style={{ fontSize: 11 }} />
                Allocate Resource
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AllocateResourceModal;